import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaChevronDown, FaChevronUp, FaQuestionCircle, FaSearch } from 'react-icons/fa';

const FAQs = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const faqData = [
    {
      id: 1,
      question: "What are the school hours and schedule?",
      answer: "Our school operates from 8:00 AM to 3:30 PM, Monday through Friday. We also offer extended care programs from 7:00 AM to 6:00 PM for working parents. The academic year runs from September to June with scheduled breaks for holidays and teacher professional development days.",
      category: "General",
      tags: ["schedule", "hours", "timing"]
    },
    {
      id: 2,
      question: "How do I enroll my child in the school?",
      answer: "Enrollment typically begins in January for the following academic year. You'll need to complete an application form, provide required documents (birth certificate, immunization records, previous school records), and attend an interview. We also offer campus tours and open house events throughout the year.",
      category: "Admissions",
      tags: ["enrollment", "application", "admission"]
    },
    {
      id: 3,
      question: "What extracurricular activities are available?",
      answer: "We offer a wide range of extracurricular activities including sports (soccer, basketball, swimming), arts (drama, music, visual arts), academic clubs (science, math, debate), and community service programs. Activities are available for all grade levels and are included in tuition fees.",
      category: "Activities",
      tags: ["extracurricular", "sports", "arts", "clubs"]
    },
    {
      id: 4,
      question: "What is the student-teacher ratio?",
      answer: "We maintain an average student-teacher ratio of 15:1 in primary grades and 18:1 in secondary grades. This allows for personalized attention and effective learning. Our maximum class size is 25 students to ensure quality education.",
      category: "Academic",
      tags: ["class size", "teacher ratio", "personalized"]
    },
    {
      id: 5,
      question: "How do you handle bullying and discipline?",
      answer: "We have a comprehensive anti-bullying policy and behavior management system. Our approach focuses on positive reinforcement, conflict resolution, and character education. Serious incidents are addressed immediately with clear consequences and parent communication.",
      category: "Discipline",
      tags: ["bullying", "discipline", "behavior"]
    },
    {
      id: 6,
      question: "What technology resources are available to students?",
      answer: "Our school is equipped with modern technology including computer labs, interactive whiteboards, tablets for younger students, and a 1:1 laptop program for secondary students. We also offer coding classes, digital literacy programs, and online learning platforms.",
      category: "Technology",
      tags: ["computers", "laptops", "digital", "coding"]
    },
    {
      id: 7,
      question: "How do you support students with special needs?",
      answer: "We provide comprehensive support for students with diverse learning needs through our Special Education department. This includes individualized education plans (IEPs), learning support specialists, speech therapy, occupational therapy, and accommodations as needed.",
      category: "Support",
      tags: ["special needs", "IEP", "support", "therapy"]
    },
    {
      id: 8,
      question: "What is the school's approach to homework?",
      answer: "We believe in purposeful homework that reinforces learning without overwhelming students. Homework expectations vary by grade level: 15-30 minutes for primary grades, 45-60 minutes for middle grades, and 1-2 hours for secondary students. We emphasize quality over quantity.",
      category: "Academic",
      tags: ["homework", "assignments", "study"]
    },
    {
      id: 9,
      question: "How do you communicate with parents?",
      answer: "We maintain regular communication through multiple channels: monthly newsletters, parent-teacher conferences (3 times per year), weekly classroom updates, our school app, and an open-door policy for concerns. We also host parent education workshops and family events.",
      category: "Communication",
      tags: ["parent", "communication", "conferences"]
    },
    {
      id: 10,
      question: "What are the tuition fees and payment options?",
      answer: "Tuition fees vary by grade level and are set annually. We offer flexible payment plans including monthly, quarterly, and annual options. Financial aid and scholarships are available for qualifying families. All fees include textbooks, basic supplies, and most extracurricular activities.",
      category: "Financial",
      tags: ["tuition", "fees", "payment", "financial aid"]
    },
    {
      id: 11,
      question: "How do you prepare students for college and careers?",
      answer: "Our college and career readiness program includes academic counseling, standardized test preparation, college application guidance, career exploration activities, internships, and partnerships with local businesses and universities. We have a 100% college acceptance rate.",
      category: "College",
      tags: ["college", "career", "counseling", "preparation"]
    },
    {
      id: 12,
      question: "What safety measures are in place?",
      answer: "Student safety is our top priority. We have secure campus access, regular safety drills, trained security personnel, comprehensive emergency protocols, and a zero-tolerance policy for weapons or threats. All staff undergo background checks and safety training.",
      category: "Safety",
      tags: ["security", "safety", "emergency", "drills"]
    }
  ];

  const categories = [
    { id: 'all', name: 'All Questions', color: 'gray' },
    { id: 'general', name: 'General', color: 'blue' },
    { id: 'admissions', name: 'Admissions', color: 'green' },
    { id: 'academic', name: 'Academic', color: 'purple' },
    { id: 'activities', name: 'Activities', color: 'orange' },
    { id: 'financial', name: 'Financial', color: 'red' },
    { id: 'safety', name: 'Safety', color: 'indigo' },
    { id: 'support', name: 'Support', color: 'pink' },
    { id: 'discipline', name: 'Discipline', color: 'yellow' },
    { id: 'technology', name: 'Technology', color: 'teal' },
    { id: 'communication', name: 'Communication', color: 'cyan' },
    { id: 'college', name: 'College Prep', color: 'emerald' }
  ];

  const getColorClasses = (color) => {
    const colors = {
      gray: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
      blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      green: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      red: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      pink: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
      yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      teal: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
      cyan: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
      emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
    };
    return colors[color] || colors.gray;
  };

  const filteredFAQs = faqData.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || faq.category.toLowerCase() === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleFaq = (id) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
      <Helmet>
        <title>Rasoha Academy | FAQs - Frequently Asked Questions</title>
        <meta name="description" content="Find answers to frequently asked questions about Rasoha Academy. Get information about admissions, school policies, programs, and general inquiries." />
        <meta name="keywords" content="Rasoha Academy FAQs, School Questions, Admissions FAQ, School Policies, Academic Programs, School Information" />
        <meta property="og:title" content="Rasoha Academy | FAQs - Frequently Asked Questions" />
        <meta property="og:description" content="Find answers to frequently asked questions about Rasoha Academy. Get information about admissions, school policies, and programs." />
        <meta property="og:url" content="https://rasohaacademy.com/faqs" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-aos="fade-up">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-90">
            Find answers to common questions about our school, programs, and policies
          </p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between" data-aos="fade-up">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Results Count */}
          <div className="text-center mb-8" data-aos="fade-up">
            <p className="text-gray-600 dark:text-gray-300">
              Showing {filteredFAQs.length} of {faqData.length} questions
            </p>
          </div>

          {/* FAQs Accordion */}
          <div className="space-y-4">
            {filteredFAQs.map((faq, index) => (
              <div
                key={faq.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <button
                  onClick={() => toggleFaq(faq.id)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-4">
                    <FaQuestionCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                        {faq.question}
                      </h3>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-2 ${getColorClasses(categories.find(c => c.id === faq.category.toLowerCase())?.color)}`}>
                        {faq.category}
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    {openFaq === faq.id ? (
                      <FaChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200" />
                    ) : (
                      <FaChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200" />
                    )}
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openFaq === faq.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-4">
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredFAQs.length === 0 && (
            <div className="text-center py-12" data-aos="fade-up">
              <FaQuestionCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                No questions found
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Try adjusting your search terms or category filter.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-aos="fade-up">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Still Have Questions?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Can't find what you're looking for? Our team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              Contact Us
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300 transform hover:scale-105">
              Schedule a Tour
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQs; 